import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '../../lib/supabase/client';
import DashboardLayout from '../components/layout/DashboardLayout';
import DashboardStats from '../components/dashboard/DashboardStats';
import RevenueChart from '../components/dashboard/RevenueChart';
import ProjectsStatus from '../components/dashboard/ProjectsStatus';
import OverdueInvoicesAlert from '../components/dashboard/OverdueInvoicesAlert';
import '../components/styles.css';

export default function Dashboard() {
  const navigate = useNavigate();
  const [revenueChartData, setRevenueChartData] = useState([]);
  const [projectsStatusData, setProjectsStatusData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const supabase = createClient();
        const { data: { user: currentUser } } = await supabase.auth.getUser();

        if (!currentUser) {
          navigate('/crm/login');
          return;
        }

        // Récupérer les données pour RevenueChart
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);

        const { data: invoices, error: invoicesError } = await supabase
          .from('invoices')
          .select('total_amount, paid_at, status')
          .eq('status', 'paid')
          .gte('paid_at', sixMonthsAgo.toISOString())
          .limit(500);

        if (invoicesError) {
          console.error('Erreur lors de la récupération des factures:', invoicesError);
        }

        // Grouper par mois
        const monthlyData = new Map();
        const months = [];
        for (let i = 5; i >= 0; i--) {
          const date = new Date();
          date.setMonth(date.getMonth() - i);
          const monthKey = date.toLocaleDateString('fr-FR', {
            month: 'short',
            year: 'numeric',
          });
          months.push(monthKey);
          monthlyData.set(monthKey, 0);
        }

        invoices?.forEach((invoice) => {
          if (invoice.paid_at) {
            const date = new Date(invoice.paid_at);
            const monthKey = date.toLocaleDateString('fr-FR', {
              month: 'short',
              year: 'numeric',
            });
            const current = monthlyData.get(monthKey) || 0;
            monthlyData.set(monthKey, current + (invoice.total_amount || 0));
          }
        });

        const chartData = months.map((month) => ({
          month,
          revenue: monthlyData.get(month) || 0,
        }));
        setRevenueChartData(chartData);

        // Récupérer les données pour ProjectsStatus
        const { data: projects, error: projectsError } = await supabase
          .from('projects')
          .select('status')
          .limit(1000);

        if (projectsError) {
          console.error('Erreur lors de la récupération des projets:', projectsError);
        }

        const statusCounts = {
          draft: 0,
          in_progress: 0,
          completed: 0,
          cancelled: 0,
        };

        projects?.forEach((project) => {
          statusCounts[project.status] = (statusCounts[project.status] || 0) + 1;
        });

        const COLORS = {
          draft: '#94a3b8',
          in_progress: '#3b82f6',
          completed: '#22c55e',
          cancelled: '#ef4444',
        };

        const statusData = [
          { name: 'Brouillon', value: statusCounts.draft, color: COLORS.draft },
          { name: 'En cours', value: statusCounts.in_progress, color: COLORS.in_progress },
          { name: 'Terminé', value: statusCounts.completed, color: COLORS.completed },
          { name: 'Annulé', value: statusCounts.cancelled, color: COLORS.cancelled },
        ].filter((item) => item.value > 0);
        setProjectsStatusData(statusData);
      } catch (error) {
        console.error('Erreur lors du chargement du dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Chargement...</div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="mt-2 text-gray-600">
            Vue d'ensemble de votre activité
          </p>
        </div>

        <DashboardStats />
        <OverdueInvoicesAlert />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <RevenueChart data={revenueChartData} />
          <ProjectsStatus data={projectsStatusData} />
        </div>
      </div>
    </DashboardLayout>
  );
}

